import { Children, type Ref, isValidElement } from 'react';

import { cloneElement, isOutOfViewport } from '@/util';

import {
  POPOVER_GAP,
  POPOVER_PLACEMENT,
  POPOVER_PLACEMENT_FLIPMAP,
} from './constant';
import type { VBasePopoverContextValue } from './type';

const genPlacementMap = (target: HTMLElement, popover: HTMLElement) => {
  const {
    top: tT,
    left: tL,
    bottom: tB,
    right: tR,
    width: tW,
    height: tH,
  } = target.getBoundingClientRect();
  const { width: pW, height: pH } = popover.getBoundingClientRect();

  const placementMap = {
    get [POPOVER_PLACEMENT.TOP]() {
      const top = tT - pH - POPOVER_GAP;
      return {
        center: { top, left: tL + tW / 2 - pW / 2 },
        left: { top, left: tL },
        right: { top, left: tR - pW },
      };
    },
    get [POPOVER_PLACEMENT.BOTTOM]() {
      const top = tB + POPOVER_GAP;
      return {
        center: { top, left: tL + tW / 2 - pW / 2 },
        left: { top, left: tL },
        right: { top, left: tR - pW },
      };
    },
    get [POPOVER_PLACEMENT.LEFT]() {
      const left = tL - pW - POPOVER_GAP;
      return {
        center: { top: tT + tH / 2 - pH / 2, left },
        top: { top: tT, left },
        bottom: { top: tB - pH, left },
      };
    },
    get [POPOVER_PLACEMENT.RIGHT]() {
      const left = tR + POPOVER_GAP;
      return {
        center: { top: tT + tH / 2 - pH / 2, left },
        top: { top: tT, left },
        bottom: { top: tB - pH, left },
      };
    },
  };

  return placementMap;
};

const getFlippedPlacement = (
  placement: VBasePopoverContextValue['placement'],
) => POPOVER_PLACEMENT_FLIPMAP[placement] ?? placement;

export const getPosition = ({
  placement,
  $popover,
  $target,
}: {
  placement: VBasePopoverContextValue['placement'];
  $popover: HTMLDivElement;
  $target: HTMLElement;
}): {
  top: number;
  left: number;
} => {
  let position = { top: 0, left: 0 };
  const { width: pW, height: pH } = $popover.getBoundingClientRect();
  const placementMap = genPlacementMap($target, $popover);

  const applyPlacement = (placement: VBasePopoverContextValue['placement']) => {
    const second = placement.match(/(?<=-)\w+$/)?.[0] ?? 'center';
    const first = (
      second ? placement.replace(`-${second}`, '') : placement
    ) as keyof typeof placementMap;
    const { top, left } = (placementMap[first] as any)[second];

    return { top: Math.max(top, 0), left: Math.max(left, 0) };
  };

  position = applyPlacement(placement);

  if (isOutOfViewport({ ...position, width: pW, height: pH })) {
    const flipped = getFlippedPlacement(placement);

    position = applyPlacement(flipped);
  }

  return { top: Math.max(position.top, 0), left: Math.max(position.left, 0) };
};

export const getChildren = <T>(children: React.ReactNode, ref: Ref<T>) => {
  const child = Children.only(children);

  if (!isValidElement(child)) return child;

  const originalProps = child.props ?? ({} as any);

  const childProps = {
    ...originalProps,
    ref,
  };

  return cloneElement(child, childProps);
};
